import { createContext, useContext, useCallback } from 'react';

export const LANGS = [
  { code: 'en', label: 'English' },
  { code: 'zh', label: '汉语' },
  { code: 'es', label: 'español' },
];

// UI label dictionary. Keys are grouped by area. Only UI labels are translated —
// user data (contacts, properties, message content, etc.) is never translated.
const DICT = {
  en: {
    // Navigation
    'nav.contacts': 'Contacts',
    'nav.properties': 'Properties',
    'nav.templates': 'Reminder Templates',
    'nav.bulkAssign': 'Assign',
    'nav.assigned': 'Assign Reminders',
    'nav.run': 'Reminder History',
    'nav.mls': 'Supported MLS',
    'nav.files': 'Files',
    'nav.agents': 'Manage Agents',
    'nav.messageAgents': 'Message Agents',
    'nav.messageContacts': 'Message Contacts',
    'nav.process': 'Process',
    'nav.users': 'Users',
    // Header / common
    'header.profile': 'Profile',
    'common.language': 'Language',
    // Landing
    'landing.signin': 'Sign In',
    'landing.eyebrow': 'For real estate agents',
    'landing.title': 'Win more referrals by never losing touch.',
    'landing.sub': 'iBetterAgent helps you nurture every client relationship — automatically. Organize your contacts and closed deals, then let the app send timely, personal reminders so you stay connected long after the sale.',
    'landing.getStarted': 'Get Started',
    'landing.ctaNote': 'Set up in minutes',
    'landing.f1.title': 'All your clients, organized',
    'landing.f1.text': 'Import contacts and closed properties in seconds. Every client, deal, and detail lives in one tidy place.',
    'landing.f2.title': 'Reminders that send themselves',
    'landing.f2.text': 'Birthdays, sale anniversaries, and New Year greetings go out automatically — personalized, on time, every time.',
    'landing.f3.title': 'More selling, less remembering',
    'landing.f3.text': 'Stop juggling spreadsheets and sticky notes. The app handles your follow-ups so you can focus on closing.',
    'landing.f4.title': 'Stay top-of-mind',
    'landing.f4.text': 'Consistent, thoughtful touchpoints keep you the agent clients think of first — and the one they refer to friends.',
    'landing.connect.title': 'Your next deal is hiding in your past clients.',
    'landing.connect.text': 'Most of an agent’s business comes from repeat clients and referrals — yet relationships go cold within months of closing. A simple “happy birthday” or “happy one year in your home” is often all it takes to stay the agent they remember. iBetterAgent makes those moments effortless and consistent, turning one-time buyers into lifelong clients and a steady stream of referrals.',
    'landing.connect.cta': 'Start staying connected',
    'landing.footer': '🤝 iBetterAgent — built for agents who play the long game.',
    // Info links
    'info.why': 'Why iBetterAgent',
    'info.about': 'About',
    'info.pricing': 'Pricing',
    'info.help': 'Help',
    'info.terms': 'Terms of Use',
    'info.privacy': 'Privacy Notice',
    'info.contact': 'Contact Us',
    'info.back': '← Back to home',
    // Login
    'login.back': '← Back to home',
    'login.label.organization': 'Organization',
    'login.label.systemAdmin': 'System Administrator (iBetterAgent)',
    'login.label.independent': 'Independent agent (just me)',
    'login.signupNote': "Signing up creates your own individual account with iBetterAgent. Pick your MLS so your imports and reminders match it.",
    'login.label.mls': 'Your MLS',
    'login.mlsPlaceholder': '— Select your MLS —',
    'login.mlsHint': 'Choose the MLS you belong to. This sets the correct import format for your listings.',
    'login.pwHint': 'At least 8 characters, including an uppercase letter, a lowercase letter, and a number.',
    'login.pwMismatch': 'Passwords do not match.',
    'login.title.login': 'Sign In',
    'login.title.signup': 'Create Your Account',
    'login.title.reset': 'Reset Password',
    'login.label.username': 'Login Email',
    'login.label.userName': 'User Name',
    'login.label.yourUsername': 'Login Email',
    'login.label.password': 'Password',
    'login.label.confirm': 'Confirm Password',
    'login.label.emailReset': 'Email (saved to your account)',
    'login.view': '👁 View',
    'login.hide': '🙈 Hide',
    'login.btn.login': 'Log In',
    'login.btn.signup': 'Sign Up',
    'login.btn.reset': 'Send Reset Link',
    'login.btn.sending': 'Sending…',
    'login.forgot': 'Forgot password?',
    'login.noAccount': "Don't have an account?",
    'login.haveAccount': 'Already have an account?',
    'login.signupLink': 'Sign up',
    'login.loginLink': 'Log in',
    'login.adminHint': 'Admin login:',
  },
  zh: {
    'nav.contacts': '联系人',
    'nav.properties': '房产',
    'nav.templates': '提醒模板',
    'nav.bulkAssign': '分配',
    'nav.assigned': '分配提醒',
    'nav.run': '提醒历史',
    'nav.mls': '支持的 MLS',
    'nav.files': '文件',
    'nav.agents': '管理代理',
    'nav.messageAgents': '发送消息',
    'nav.messageContacts': '群发联系人',
    'nav.process': '处理',
    'nav.users': '用户',
    'header.profile': '个人资料',
    'common.language': '语言',
    'landing.signin': '登录',
    'landing.eyebrow': '致房地产经纪人',
    'landing.title': '保持联系，赢得更多推荐。',
    'landing.sub': 'iBetterAgent 帮助您自动维护每一段客户关系。整理您的联系人和成交记录，让应用及时发送个性化提醒，让您在成交后依然与客户保持联系。',
    'landing.getStarted': '开始使用',
    'landing.ctaNote': '几分钟即可设置',
    'landing.f1.title': '井井有条的客户管理',
    'landing.f1.text': '几秒钟即可导入联系人和成交房产。每个客户、交易和细节都集中一处，井然有序。',
    'landing.f2.title': '自动发送的提醒',
    'landing.f2.text': '生日、成交周年和新年问候都会自动发出——个性化、准时、从不遗漏。',
    'landing.f3.title': '多成交，少操心',
    'landing.f3.text': '告别繁杂的表格和便利贴。应用替您处理跟进，让您专注于成交。',
    'landing.f4.title': '让客户始终记得您',
    'landing.f4.text': '持续而贴心的互动，让您成为客户首先想到、并乐于推荐给朋友的经纪人。',
    'landing.connect.title': '您的下一笔交易，就藏在过往客户之中。',
    'landing.connect.text': '经纪人的大部分业务来自回头客和推荐——然而关系往往在成交后数月内冷却。一句简单的“生日快乐”或“乔迁一周年快乐”，往往就足以让您成为客户记得的经纪人。iBetterAgent 让这些时刻轻松而持续，把一次性买家变成终身客户和源源不断的推荐。',
    'landing.connect.cta': '开始保持联系',
    'landing.footer': '🤝 iBetterAgent — 为放眼长远的经纪人打造。',
    'info.why': '为什么选择 iBetterAgent',
    'info.about': '关于',
    'info.pricing': '价格',
    'info.help': '帮助',
    'info.terms': '使用条款',
    'info.privacy': '隐私声明',
    'info.contact': '联系我们',
    'info.back': '← 返回首页',
    'login.back': '← 返回首页',
    'login.label.organization': '组织',
    'login.label.systemAdmin': '系统管理员（iBetterAgent）',
    'login.label.independent': '独立经纪人（仅我自己）',
    'login.signupNote': '注册将在 iBetterAgent 创建您的个人账户。请选择您的 MLS，以便导入和提醒与之匹配。',
    'login.label.mls': '您的 MLS',
    'login.mlsPlaceholder': '— 请选择您的 MLS —',
    'login.mlsHint': '选择您所属的 MLS，这将为您的房源设置正确的导入格式。',
    'login.pwHint': '至少 8 个字符，需包含一个大写字母、一个小写字母和一个数字。',
    'login.pwMismatch': '两次输入的密码不一致。',
    'login.title.login': '登录',
    'login.title.signup': '创建您的账户',
    'login.title.reset': '重置密码',
    'login.label.username': '登录邮箱',
    'login.label.userName': '用户名',
    'login.label.yourUsername': '登录邮箱',
    'login.label.password': '密码',
    'login.label.confirm': '确认密码',
    'login.label.emailReset': '电子邮箱（将保存到您的账户）',
    'login.view': '👁 显示',
    'login.hide': '🙈 隐藏',
    'login.btn.login': '登录',
    'login.btn.signup': '注册',
    'login.btn.reset': '发送重置链接',
    'login.btn.sending': '发送中…',
    'login.forgot': '忘记密码？',
    'login.noAccount': '还没有账户？',
    'login.haveAccount': '已有账户？',
    'login.signupLink': '注册',
    'login.loginLink': '登录',
    'login.adminHint': '管理员登录：',
  },
  es: {
    'nav.contacts': 'Contactos',
    'nav.properties': 'Propiedades',
    'nav.templates': 'Plantillas de recordatorio',
    'nav.bulkAssign': 'Asignar',
    'nav.assigned': 'Asignar recordatorios',
    'nav.run': 'Historial de recordatorios',
    'nav.mls': 'MLS compatibles',
    'nav.files': 'Archivos',
    'nav.agents': 'Gestionar agentes',
    'nav.messageAgents': 'Mensaje a agentes',
    'nav.messageContacts': 'Mensaje a contactos',
    'nav.process': 'Procesar',
    'nav.users': 'Usuarios',
    'header.profile': 'Perfil',
    'common.language': 'Idioma',
    'landing.signin': 'Iniciar sesión',
    'landing.eyebrow': 'Para agentes inmobiliarios',
    'landing.title': 'Gana más referencias sin perder el contacto.',
    'landing.sub': 'iBetterAgent te ayuda a cultivar cada relación con tus clientes, de forma automática. Organiza tus contactos y operaciones cerradas, y deja que la app envíe recordatorios personalizados y oportunos para seguir en contacto mucho después de la venta.',
    'landing.getStarted': 'Comenzar',
    'landing.ctaNote': 'Configúralo en minutos',
    'landing.f1.title': 'Todos tus clientes, organizados',
    'landing.f1.text': 'Importa contactos y propiedades vendidas en segundos. Cada cliente, operación y detalle en un solo lugar ordenado.',
    'landing.f2.title': 'Recordatorios que se envían solos',
    'landing.f2.text': 'Cumpleaños, aniversarios de venta y saludos de Año Nuevo se envían automáticamente: personalizados y puntuales, siempre.',
    'landing.f3.title': 'Vende más, recuerda menos',
    'landing.f3.text': 'Olvídate de hojas de cálculo y notas adhesivas. La app gestiona tus seguimientos para que te concentres en cerrar.',
    'landing.f4.title': 'Mantente presente',
    'landing.f4.text': 'Un contacto constante y atento te convierte en el agente que tus clientes recuerdan primero, y al que recomiendan.',
    'landing.connect.title': 'Tu próxima operación está entre tus clientes anteriores.',
    'landing.connect.text': 'La mayor parte del negocio de un agente proviene de clientes recurrentes y referencias, pero las relaciones se enfrían a los pocos meses del cierre. Un simple “feliz cumpleaños” o “feliz primer año en tu hogar” suele bastar para seguir siendo el agente que recuerdan. iBetterAgent hace que esos momentos sean fáciles y constantes, convirtiendo a compradores ocasionales en clientes de por vida y en un flujo constante de referencias.',
    'landing.connect.cta': 'Empieza a mantener el contacto',
    'landing.footer': '🤝 iBetterAgent — creado para agentes que juegan a largo plazo.',
    'info.why': 'Por qué iBetterAgent',
    'info.about': 'Acerca de',
    'info.pricing': 'Precios',
    'info.help': 'Ayuda',
    'info.terms': 'Términos de uso',
    'info.privacy': 'Aviso de privacidad',
    'info.contact': 'Contáctanos',
    'info.back': '← Volver al inicio',
    'login.back': '← Volver al inicio',
    'login.label.organization': 'Organización',
    'login.label.systemAdmin': 'Administrador del sistema (iBetterAgent)',
    'login.label.independent': 'Agente independiente (solo yo)',
    'login.signupNote': 'Al registrarte creas tu cuenta individual con iBetterAgent. Elige tu MLS para que tus importaciones y recordatorios coincidan.',
    'login.label.mls': 'Tu MLS',
    'login.mlsPlaceholder': '— Selecciona tu MLS —',
    'login.mlsHint': 'Elige el MLS al que perteneces. Esto define el formato de importación correcto para tus propiedades.',
    'login.pwHint': 'Al menos 8 caracteres, con una mayúscula, una minúscula y un número.',
    'login.pwMismatch': 'Las contraseñas no coinciden.',
    'login.title.login': 'Inicio de sesión',
    'login.title.signup': 'Crea tu cuenta',
    'login.title.reset': 'Restablecer contraseña',
    'login.label.username': 'Correo de acceso',
    'login.label.userName': 'Nombre de usuario',
    'login.label.yourUsername': 'Correo de acceso',
    'login.label.password': 'Contraseña',
    'login.label.confirm': 'Confirmar contraseña',
    'login.label.emailReset': 'Correo electrónico (se guardará en tu cuenta)',
    'login.view': '👁 Ver',
    'login.hide': '🙈 Ocultar',
    'login.btn.login': 'Iniciar sesión',
    'login.btn.signup': 'Registrarse',
    'login.btn.reset': 'Enviar enlace',
    'login.btn.sending': 'Enviando…',
    'login.forgot': '¿Olvidaste tu contraseña?',
    'login.noAccount': '¿No tienes cuenta?',
    'login.haveAccount': '¿Ya tienes cuenta?',
    'login.signupLink': 'Regístrate',
    'login.loginLink': 'Inicia sesión',
    'login.adminHint': 'Acceso de administrador:',
  },
};

const I18nContext = createContext({ lang: 'en', setLang: () => {}, t: (k) => k });

export function I18nProvider({ children }) {
  // The language switcher is hidden — the app is English-only for now. `lang` is fixed
  // to 'en' and setLang is a no-op, so a stale saved preference can't surface another
  // language. The other-language dictionaries above stay dormant so multi-language can
  // be re-enabled later by restoring the switcher.
  const lang = 'en';
  const setLang = useCallback(() => {}, []);
  const t = useCallback((key) => DICT.en[key] || key, []);
  return <I18nContext.Provider value={{ lang, setLang, t }}>{children}</I18nContext.Provider>;
}

export function useI18n() { return useContext(I18nContext); }
